import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type InstallTenantTemplateBody,
  type MigrateTenantTemplateBody,
  type TenantTemplateListParams,
  type ToggleEnabledBody,
  adminClient,
} from '../lib/admin-client.js';

// N-10: correct invalidation key for tenant templates
const TENANT_TEMPLATE_KEYS = {
  all: (tenantId: string) => ['admin-tenant-templates', tenantId] as const,
  list: (tenantId: string, params: TenantTemplateListParams) =>
    ['admin-tenant-templates', tenantId, 'list', params] as const,
};

// B-4: query hook that calls the correct tenant-scoped endpoint
export function useAdminTenantTemplates(tenantId: string, params: TenantTemplateListParams = {}) {
  return useQuery({
    queryKey: TENANT_TEMPLATE_KEYS.list(tenantId, params),
    queryFn: () => adminClient.listTenantTemplates(tenantId, params),
    enabled: tenantId.length > 0,
    staleTime: 30_000,
  });
}

export function useInstallTenantTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstallTenantTemplateBody) =>
      adminClient.installTenantTemplate(tenantId, body),
    onSuccess: () => {
      // N-10: invalidate tenant-specific key
      void qc.invalidateQueries({ queryKey: TENANT_TEMPLATE_KEYS.all(tenantId) });
    },
  });
}

export function useMigrateTenantTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantReportId,
      body,
    }: {
      tenantReportId: string;
      body: MigrateTenantTemplateBody;
    }) => adminClient.migrateTenantTemplate(tenantId, tenantReportId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_TEMPLATE_KEYS.all(tenantId) });
    },
  });
}

export function useUninstallTenantTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantReportId: string) =>
      adminClient.uninstallTenantTemplate(tenantId, tenantReportId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_TEMPLATE_KEYS.all(tenantId) });
    },
  });
}

export function useToggleEnabled(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantReportId,
      body,
    }: {
      tenantReportId: string;
      body: ToggleEnabledBody;
    }) => adminClient.toggleEnabled(tenantId, tenantReportId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TENANT_TEMPLATE_KEYS.all(tenantId) });
    },
  });
}
